import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bed, Bath, Maximize, FileText, X } from 'lucide-react';
import { Model, Elevation, ExteriorPalette } from '../../types';
import { ElevationStrip } from './ElevationStrip';
import { PaletteSelector } from '../Configuration/PaletteSelector';
interface ModelCardProps {
  model: Model;
  selectedElevationId?: string;
  selectedPaletteId?: string;
  palettes?: ExteriorPalette[];
  onSelectModel: (model: Model) => void;
  onSelectElevation: (elevation: Elevation) => void;
  onSelectPalette?: (palette: ExteriorPalette) => void;
  layout?: 'vertical' | 'horizontal';
}
export function ModelCard({
  model,
  selectedElevationId,
  selectedPaletteId,
  palettes,
  onSelectModel,
  onSelectElevation,
  onSelectPalette,
  layout = 'vertical'
}: ModelCardProps) {
  const [showFloorplans, setShowFloorplans] = useState(false);
  const isExpanded = !!selectedElevationId;
  const selectedElevation = model.elevations.find(e => e.id === selectedElevationId);
  const hasFloorplans = selectedElevation?.floorplans && selectedElevation.floorplans.length > 0;
  return <motion.div layout className="bg-slate-800/30 rounded-xl border border-white/5 overflow-hidden" data-id="element-1148">
      {/* Header - Always Visible */}
      <button onClick={() => onSelectModel(model)} className="w-full text-left hover:bg-slate-800/50 transition-colors" data-id="element-1149">
        {/* Model Image Preview */}
        <div className="relative h-48 overflow-hidden" data-id="element-1150">
          <img src={selectedElevation?.thumbnailUrl || model.elevations[0].thumbnailUrl} alt={model.name} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" data-id="element-1151" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" data-id="element-1152" />

          {/* Price Badge */}
          <div className="absolute top-3 right-3 px-3 py-1 rounded-lg bg-slate-900/90 backdrop-blur-sm border border-white/10" data-id="element-1153">
            <p className="text-sm font-bold text-cyan-400" data-id="element-1154">
              ${(model.price / 1000).toFixed(0)}k
            </p>
          </div>
        </div>

        {/* Model Info */}
        <div className="p-4" data-id="element-1155">
          <div className="flex items-start justify-between mb-2" data-id="element-1156">
            <div data-id="element-1157">
              <h3 className="text-lg font-light text-white mb-1" data-id="element-1158">
                {model.name}
              </h3>
              <p className="text-xs text-slate-400" data-id="element-1159">{model.description}</p>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-3 text-xs text-slate-400" data-id="element-1160">
            <div className="flex items-center gap-1" data-id="element-1161">
              <Bed className="w-3.5 h-3.5" data-id="element-1162" />
              <span data-id="element-1163">{model.beds}</span>
            </div>
            <div className="flex items-center gap-1" data-id="element-1164">
              <Bath className="w-3.5 h-3.5" data-id="element-1165" />
              <span data-id="element-1166">{model.baths}</span>
            </div>
            <div className="flex items-center gap-1" data-id="element-1167">
              <Maximize className="w-3.5 h-3.5" data-id="element-1168" />
              <span data-id="element-1169">{model.sqft.toLocaleString()} sq ft</span>
            </div>
          </div>
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: 'auto'
    }} exit={{
      opacity: 0,
      height: 0
    }} className="border-t border-white/5" data-id="element-1170">
          <div className="p-4 space-y-4" data-id="element-1171">
            {/* Elevation Strip with Floorplan Button */}
            <div data-id="element-1172">
              <div className="flex items-center justify-between mb-2" data-id="element-1173">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400" data-id="element-1174">
                  Choose Elevation
                </h4>
                {hasFloorplans && <button onClick={() => setShowFloorplans(true)} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 border border-white/10 text-xs font-medium text-cyan-400 transition-colors" data-id="element-1175">
                    <FileText className="w-3.5 h-3.5" data-id="element-1176" />
                    View Floorplan
                  </button>}
              </div>
              <ElevationStrip elevations={model.elevations} selectedElevationId={selectedElevationId} onSelect={onSelectElevation} compact={false} data-id="element-1177" />
            </div>

            {/* Palette Selector - with toggle */}
            {palettes && onSelectPalette && selectedElevationId && <PaletteSelector palettes={palettes} selectedPaletteId={selectedPaletteId || null} onSelectPalette={onSelectPalette} data-id="element-1178" />}
          </div>
        </motion.div>}

      {/* Floorplan Modal */}
      <AnimatePresence data-id="element-1179">
        {showFloorplans && hasFloorplans && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setShowFloorplans(false)} data-id="element-1180">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} className="relative bg-slate-900 rounded-xl border border-white/10 shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-1181">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/10" data-id="element-1182">
                <div data-id="element-1183">
                  <h3 className="text-lg font-bold text-white" data-id="element-1184">
                    {model.name} - Elevation {selectedElevation.letter}
                  </h3>
                  <p className="text-xs text-slate-400" data-id="element-1185">
                    {selectedElevation.name} Floorplan
                  </p>
                </div>
                <button onClick={() => setShowFloorplans(false)} className="p-2 rounded-full hover:bg-white/10 text-slate-400 hover:text-white transition-colors" data-id="element-1186">
                  <X className="w-5 h-5" data-id="element-1187" />
                </button>
              </div>

              {/* Floorplan Images */}
              <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)] space-y-4" data-id="element-1188">
                {selectedElevation.floorplans!.map((floorplan, index) => <div key={index} className="relative" data-id="element-1189">
                    <img src={floorplan} alt={`Floorplan ${index + 1}`} className="w-full h-auto rounded-lg" data-id="element-1190" />
                    {selectedElevation.floorplans!.length > 1 && <div className="absolute top-2 left-2 px-2 py-1 bg-slate-900/90 backdrop-blur-sm rounded text-xs font-bold text-white" data-id="element-1191">
                        Floor {index + 1}
                      </div>}
                  </div>)}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}